import { debounce } from 'lodash-es';
import { ref } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';
import CatalogService from './services/CatalogService';

interface ElementProperties {
    readonly url: string;
    readonly top: number;
    readonly left: number;
    readonly width: number;
    readonly height: number;
}

export interface Options {
    readonly size: number;
    readonly freezeMode: boolean;
}

interface IContentZoom {
    element: HTMLElement;
    elementProperties: ElementProperties;
    options: Options;
    isLoaded: boolean;
}

const defaultConfig: Options = {
    size: 250,
    freezeMode: false
};

const getElementProperties = (element: HTMLElement) : ElementProperties => {
    const clientRect = element.getBoundingClientRect();

    return {
        top: clientRect.top + window.scrollY,
        left: clientRect.left + window.scrollX,
        width: element.clientWidth,
        height: element.clientHeight,
        url: element.getAttribute('data-resource') as string
    };
};

const isInside = (properties: ElementProperties, pageX: number, pageY: number) : boolean => {
    const tolerance = 20;
    const left = pageX - properties.left;
    const top = pageY - properties.top;

    return (left <= properties.width + tolerance && top <= properties.height + tolerance && left >= -tolerance && top >= -tolerance);
};

const createElementFromHTML = (html: string, options: Options) : [DocumentFragment, HTMLElement, HTMLElement] => {
    const documentFragment = new DocumentFragment();

    const container = document.createElement('div');
    container.className = 'zoom-container';
    container.style.width = options.size + 'px';
    container.style.height = options.size + 'px';

    const zoomInner = document.createElement('div');
    zoomInner.className = 'zoom-inner';
    zoomInner.innerHTML = html.trim();

    container.appendChild(zoomInner);
    documentFragment.appendChild(container);

    const resourceElement = zoomInner.firstElementChild as HTMLElement;

    return [documentFragment, container, resourceElement];
};

class ZoomPreview {
    container: HTMLElement;
    resourceElement: HTMLElement;
    contentZoom: IContentZoom;
    readonly halfSize: number;
    private visibleClassName = 'content-zoom-visible';
    width: number = 0;
    height: number = 0;
    isFrozen: boolean = false;

    observerStopFn: () => void;

    mouseClickListener: (e: MouseEvent) => void;
    mouseMoveListener: (e: MouseEvent) => void;
    touchStartListener: (e: TouchEvent) => void;
    touchMoveListener: (e: TouchEvent) => void;

    constructor(container: HTMLElement, resourceElement: HTMLElement, contentZoom: IContentZoom) {
        this.container = container;
        this.resourceElement = resourceElement;
        this.contentZoom = contentZoom;
        this.halfSize = 0.5 * contentZoom.options.size;

        this.container.classList.add(this.visibleClassName);

        this.recalculate();

        const zoomElement = ref(contentZoom.element);

        const { stop } = useIntersectionObserver(zoomElement, ([{ isIntersecting }]) => {
            if (isIntersecting === false) {
                this.clear();
            }
        });

        this.observerStopFn = stop;

        this.mouseClickListener = async(e: MouseEvent) : Promise<void> => {
            e.preventDefault();

            if (this.contentZoom.options.freezeMode) {
                this.toggleFreeze();
            }
        };

        this.mouseMoveListener = (e: MouseEvent) : void => {
            if (isInside(this.contentZoom.elementProperties, e.pageX, e.pageY)) {
                this.transform(e.pageX, e.pageY);
            } else {
                this.clear();
            }
        };

        this.touchStartListener = (e: TouchEvent): void => {
            e.preventDefault();

            if (this.contentZoom.options.freezeMode) {
                this.toggleFreeze();
            }
        };

        this.touchMoveListener = (e: TouchEvent): void => {
            const touch = e.touches[0];
            if (touch) {
                if (isInside(this.contentZoom.elementProperties, touch.pageX, touch.pageY)) {
                    this.transform(touch.pageX, touch.pageY);
                } else {
                    this.clear();
                }
            }
        };
        this.container.addEventListener('click', this.mouseClickListener);
        this.container.addEventListener('mousemove', this.mouseMoveListener);

        this.container.addEventListener('touchstart', this.touchStartListener);
        this.container.addEventListener('touchmove', this.touchMoveListener);
    }

    recalculate() : void {
        const style = getComputedStyle(this.resourceElement);

        this.width = parseInt(style.width);
        this.height = parseInt(style.height);
    }

    transform(pageX: number, pageY: number) : void {
        if (!this.container.classList.contains(this.visibleClassName)) {
            return;
        }

        const yOffset = parseInt(document.body.style.top || '0');
        const properties = this.contentZoom.elementProperties;
        const top = pageY - properties.top;
        const left = pageX - properties.left;
        const innerTop = -Math.round(top / properties.height * this.height - this.halfSize);
        const innerLeft = -Math.round(left / properties.width * this.width - this.halfSize);
        const containerTop = pageY - this.halfSize - yOffset;
        const containerLeft = pageX - this.halfSize;

        requestAnimationFrame(() => {
            this.container.style.transform = 'translate(' + containerLeft + 'px, ' + containerTop + 'px)';
            (this.container.firstElementChild as HTMLElement).style.transform = 'translate(' + innerLeft + 'px, ' + innerTop + 'px)';
        });
    }

    toggleFreeze() : void {
        if (this.isFrozen) {
            this.container.addEventListener('mousemove', this.mouseMoveListener);
            this.container.addEventListener('touchmove', this.touchMoveListener);
        } else {
            this.container.removeEventListener('mousemove', this.mouseMoveListener);
            this.container.removeEventListener('touchmove', this.touchMoveListener);
        }

        this.isFrozen = !this.isFrozen;
    }

    clear() : void {
        this.container.classList.remove(this.visibleClassName);

        this.container.removeEventListener('click', this.mouseClickListener);
        this.container.removeEventListener('mousemove', this.mouseMoveListener);

        this.container.removeEventListener('touchstart', this.touchStartListener);
        this.container.removeEventListener('touchmove', this.touchMoveListener);

        this.observerStopFn();

        setTimeout(() => {
            this.container.remove();
            this.contentZoom.isLoaded = false;
        }, 100);
    }
}

export class ContentZoom implements IContentZoom {
    element: HTMLElement;
    elementProperties: ElementProperties;
    options: Options;
    isLoaded: boolean = false;
    responseCache: string|undefined;
    zoomPreview: ZoomPreview | null = null;

    mouseClickListener: (e: MouseEvent) => void;

    readonly resizeListener = debounce(this.clear.bind(this), 250);

    constructor(element: HTMLElement, config?: Partial<Options>) {
        this.element = element;
        this.elementProperties = getElementProperties(element);
        this.options = { ...defaultConfig, ...config };

        this.mouseClickListener = async(e: MouseEvent) : Promise<void> => {
            e.preventDefault();

            if (!this.isLoaded) {
                if (!this.responseCache) {
                    this.responseCache = await CatalogService.get<string>(this.elementProperties.url);
                }

                this.createZoomPreview(this.responseCache);

                this.zoomPreview?.transform(e.pageX, e.pageY);
            }
        };

        this.element.addEventListener('click', this.mouseClickListener);
        window.addEventListener('resize', this.resizeListener, false);
    }

    destroy() : void {
        this.element.removeEventListener('click', this.mouseClickListener);
        window.removeEventListener('resize', this.resizeListener, false);
    }

    refresh() : void {
        this.elementProperties = getElementProperties(this.element);

        this.zoomPreview?.recalculate();
    }

    clear() : void {
        this.zoomPreview?.clear();
    }

    createZoomPreview(response: string) : void {
        const [fragmentDocument, container, resourceElement] = createElementFromHTML(response, this.options);

        document.body.appendChild(fragmentDocument);
        this.zoomPreview = new ZoomPreview(container, resourceElement, this);
        this.isLoaded = true;
    }
}

const memory = new WeakMap<HTMLElement, ContentZoom>();

export default function createZoom(element: HTMLElement, config?: Partial<Options>) : ContentZoom {
    const instance = memory.get(element);

    if (instance) {
        instance.refresh();

        return instance;
    }

    const newInstance = new ContentZoom(element, config);

    memory.set(element, newInstance);

    return newInstance;
}
